import { CommonModule } from '@angular/common';
import {
  type ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { ApiClientConfig } from './api-client-config';
import { AssetContractApiClient } from './entities/asset-contract.api-client';
import { AssetCostCenterLocationApiClient } from './entities/asset-cost-center-location.api-client';
import { AssetCostCenterOrderApiClient } from './entities/asset-cost-center-order.api-client';
import { AssetCostCenterApiClient } from './entities/asset-cost-center.api-client';
import { AssetInterventionPriorityApiClient } from './entities/asset-intervention-priority.api-client';
import { AssetOrAccessoryRequestApiClient } from './entities/asset-or-accessory-request.api-client';
import { AssetStatusHistoryApiClient } from './entities/asset-status-history.api-client';
import { AssetStatusApiClient } from './entities/asset-status.api-client';
import { AssetToBeTransferredOrUnloadedApiClient } from './entities/asset-to-be-transferred-or-unloaded.api-client';
import { AssetTypeApiClient } from './entities/asset-type.api-client';
import { AssetApiClient } from './entities/asset.api-client';
import { ContactApiClient } from './entities/contact.api-client';
import { ContractApiClient } from './entities/contract.api-client';
import { CorrectiveMaintenanceActionTypeApiClient } from './entities/corrective-maintenance-action-type.api-client';
import { CorrectiveMaintenanceActionApiClient } from './entities/corrective-maintenance-action.api-client';
import { CorrectiveMaintenanceReminderTypeApiClient } from './entities/corrective-maintenance-reminder-type.api-client';
import { CorrectiveMaintenanceReminderApiClient } from './entities/corrective-maintenance-reminder.api-client';
import { CorrectiveMaintenanceResultApiClient } from './entities/corrective-maintenance-result.api-client';
import { CorrectiveMaintenanceStatisticsApiClient } from './entities/corrective-maintenance-statistics.api-client';
import { CorrectiveMaintenanceStatusApiClient } from './entities/corrective-maintenance-status.api-client';
import { CorrectiveMaintenanceApiClient } from './entities/corrective-maintenance.api-client';
import { CostCenterLocationApiClient } from './entities/cost-center-location.api-client';
import { CostCenterApiClient } from './entities/cost-center.api-client';
import { CostClassApiClient } from './entities/cost-class.api-client';
import { DdtApiClient } from './entities/ddt.api-client';
import { DocumentTypeApiClient } from './entities/document-type.api-client';
import { GoodToBeTransferredOrUnloadedApiClient } from './entities/good-to-be-transferred-or-unloaded.api-client';
import { InOutAssetApiClient } from './entities/in-out-asset.api-client';
import { JobTitleApiClient } from './entities/job-title.api-client';
import { LocationApiClient } from './entities/location.api-client';
import { ManufacturerApiClient } from './entities/manufacturer.api-client';
import { MedicalGasRequestApiClient } from './entities/medical-gas-request.api-client';
import { MedicalUnitApiClient } from './entities/medical-unit.api-client';
import { OrderStatusApiClient } from './entities/order-status.api-client';
import { OrderApiClient } from './entities/order.api-client';
import { OutOfOrderAssetRequestApiClient } from './entities/out-of-order-asset-request.api-client';
import { PermissionApiClient } from './entities/permission.api-client';
import { PracticeApiClient } from './entities/practice.api-client';
import { ProvinceApiClient } from './entities/province.api-client';
import { RoleApiClient } from './entities/role.api-client';
import { ScheduledMaintenanceReminderTypeApiClient } from './entities/scheduled-maintenance-reminder-type.api-client';
import { ScheduledMaintenanceReminderApiClient } from './entities/scheduled-maintenance-reminder.api-client';
import { ScheduledMaintenanceResultApiClient } from './entities/scheduled-maintenance-result.api-client';
import { ScheduledMaintenanceStatusApiClient } from './entities/scheduled-maintenance-status.api-client';
import { ScheduledMaintenanceTypeApiClient } from './entities/scheduled-maintenance-type.api-client';
import { ScheduledMaintenanceApiClient } from './entities/scheduled-maintenance.api-client';
import { SupplierApiClient } from './entities/supplier.api-client';
import { SupplyTypeApiClient } from './entities/supply-type.api-client';
import { TakingChargeApiClient } from './entities/taking-charge.api-client';
import { TestingStatusApiClient } from './entities/testing-status.api-client';
import { TestingTypeApiClient } from './entities/testing-type.api-client';
import { TestingApiClient } from './entities/testing.api-client';
import { TrainingCertificateApiClient } from './entities/training-certificate.api-client';
import { TransferOrUnloadingRequestApiClient } from './entities/transfer-or-unloading-request.api-client';
import { UserApiClient } from './entities/user.api-client';
import { VpnRequestApiClient } from './entities/vpn-request.api-client';
import { FileApiClient } from './file.api-client';
import { StatusPageApiClient } from './status-page.api-client';

@NgModule({
  imports: [CommonModule],
})
export class ApiClientModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: ApiClientModule,
  ) {
    if (parentModule) {
      throw new Error(
        'ApiClientModule is already loaded. Import only in AppModule',
      );
    }
  }

  static forRoot(
    config: ApiClientConfig,
  ): ModuleWithProviders<ApiClientModule> {
    return {
      ngModule: ApiClientModule,
      providers: [
        {
          provide: ApiClientConfig,
          useValue: config,
        },
        StatusPageApiClient,
        FileApiClient,
        UserApiClient,
        {
          provide: 'userApiClient',
          useExisting: UserApiClient,
        },
        RoleApiClient,
        {
          provide: 'roleApiClient',
          useExisting: RoleApiClient,
        },
        PermissionApiClient,
        {
          provide: 'permissionApiClient',
          useExisting: PermissionApiClient,
        },
        SupplierApiClient,
        {
          provide: 'supplierApiClient',
          useExisting: SupplierApiClient,
        },
        MedicalUnitApiClient,
        {
          provide: 'medicalUnitApiClient',
          useExisting: MedicalUnitApiClient,
        },
        ContactApiClient,
        {
          provide: 'contactApiClient',
          useExisting: ContactApiClient,
        },
        ManufacturerApiClient,
        {
          provide: 'manufacturerApiClient',
          useExisting: ManufacturerApiClient,
        },
        ProvinceApiClient,
        {
          provide: 'provinceApiClient',
          useExisting: ProvinceApiClient,
        },
        JobTitleApiClient,
        {
          provide: 'jobTitleApiClient',
          useExisting: JobTitleApiClient,
        },
        TestingTypeApiClient,
        {
          provide: 'testingTypeApiClient',
          useExisting: TestingTypeApiClient,
        },
        TestingStatusApiClient,
        {
          provide: 'testingStatusApiClient',
          useExisting: TestingStatusApiClient,
        },
        CostClassApiClient,
        {
          provide: 'costClassApiClient',
          useExisting: CostClassApiClient,
        },
        AssetTypeApiClient,
        {
          provide: 'assetTypeApiClient',
          useExisting: AssetTypeApiClient,
        },
        SupplyTypeApiClient,
        {
          provide: 'supplyTypeApiClient',
          useExisting: SupplyTypeApiClient,
        },
        ContractApiClient,
        {
          provide: 'contractApiClient',
          useExisting: ContractApiClient,
        },
        CostCenterApiClient,
        {
          provide: 'costCenterApiClient',
          useExisting: CostCenterApiClient,
        },
        TestingApiClient,
        {
          provide: 'testingApiClient',
          useExisting: TestingApiClient,
        },
        DocumentTypeApiClient,
        {
          provide: 'documentTypeApiClient',
          useExisting: DocumentTypeApiClient,
        },
        OrderApiClient,
        {
          provide: 'orderApiClient',
          useExisting: OrderApiClient,
        },
        AssetApiClient,
        {
          provide: 'assetApiClient',
          useExisting: AssetApiClient,
        },
        AssetContractApiClient,
        {
          provide: 'assetContractApiClient',
          useExisting: AssetContractApiClient,
        },
        LocationApiClient,
        {
          provide: 'locationApiClient',
          useExisting: LocationApiClient,
        },
        CostCenterLocationApiClient,
        {
          provide: 'costCenterLocationApiClient',
          useExisting: CostCenterLocationApiClient,
        },
        AssetCostCenterLocationApiClient,
        {
          provide: 'assetCostCenterLocationApiClient',
          useExisting: AssetCostCenterLocationApiClient,
        },
        OrderStatusApiClient,
        {
          provide: 'orderStatusApiClient',
          useExisting: OrderStatusApiClient,
        },
        AssetCostCenterApiClient,
        {
          provide: 'assetCostCenterApiClient',
          useExisting: AssetCostCenterApiClient,
        },
        AssetCostCenterOrderApiClient,
        {
          provide: 'assetCostCenterOrderApiClient',
          useExisting: AssetCostCenterOrderApiClient,
        },
        ScheduledMaintenanceTypeApiClient,
        {
          provide: 'scheduledMaintenanceTypeApiClient',
          useExisting: ScheduledMaintenanceTypeApiClient,
        },
        ScheduledMaintenanceStatusApiClient,
        {
          provide: 'scheduledMaintenanceStatusApiClient',
          useExisting: ScheduledMaintenanceStatusApiClient,
        },
        ScheduledMaintenanceResultApiClient,
        {
          provide: 'scheduledMaintenanceResultApiClient',
          useExisting: ScheduledMaintenanceResultApiClient,
        },
        ScheduledMaintenanceApiClient,
        {
          provide: 'scheduledMaintenanceApiClient',
          useExisting: ScheduledMaintenanceApiClient,
        },
        AssetStatusApiClient,
        {
          provide: 'assetStatusApiClient',
          useExisting: AssetStatusApiClient,
        },
        AssetInterventionPriorityApiClient,
        {
          provide: 'assetInterventionPriorityApiClient',
          useExisting: AssetInterventionPriorityApiClient,
        },
        CorrectiveMaintenanceResultApiClient,
        {
          provide: 'correctiveMaintenanceResultApiClient',
          useExisting: CorrectiveMaintenanceResultApiClient,
        },
        CorrectiveMaintenanceStatusApiClient,
        {
          provide: 'correctiveMaintenanceStatusApiClient',
          useExisting: CorrectiveMaintenanceStatusApiClient,
        },
        CorrectiveMaintenanceApiClient,
        {
          provide: 'correctiveMaintenanceApiClient',
          useExisting: CorrectiveMaintenanceApiClient,
        },
        CorrectiveMaintenanceReminderApiClient,
        {
          provide: 'correctiveMaintenanceReminderApiClient',
          useExisting: CorrectiveMaintenanceReminderApiClient,
        },
        CorrectiveMaintenanceReminderTypeApiClient,
        {
          provide: 'correctiveMaintenanceReminderTypeApiClient',
          useExisting: CorrectiveMaintenanceReminderTypeApiClient,
        },
        CorrectiveMaintenanceActionApiClient,
        {
          provide: 'correctiveMaintenanceActionApiClient',
          useExisting: CorrectiveMaintenanceActionApiClient,
        },
        CorrectiveMaintenanceActionTypeApiClient,
        {
          provide: 'correctiveMaintenanceActionTypeApiClient',
          useExisting: CorrectiveMaintenanceActionTypeApiClient,
        },
        InOutAssetApiClient,
        {
          provide: 'inOutAssetApiClient',
          useExisting: InOutAssetApiClient,
        },
        ScheduledMaintenanceReminderTypeApiClient,
        {
          provide: 'scheduledMaintenanceReminderTypeApiClient',
          useExisting: ScheduledMaintenanceReminderTypeApiClient,
        },
        ScheduledMaintenanceReminderApiClient,
        {
          provide: 'scheduledMaintenanceReminderApiClient',
          useExisting: ScheduledMaintenanceReminderApiClient,
        },
        PracticeApiClient,
        {
          provide: 'practiceApiClient',
          useExisting: PracticeApiClient,
        },
        DdtApiClient,
        {
          provide: 'ddtApiClient',
          useExisting: DdtApiClient,
        },
        TakingChargeApiClient,
        {
          provide: 'takingChargeApiClient',
          useExisting: TakingChargeApiClient,
        },
        TrainingCertificateApiClient,
        {
          provide: 'trainingCertificateApiClient',
          useExisting: TrainingCertificateApiClient,
        },
        VpnRequestApiClient,
        {
          provide: 'vpnRequestApiClient',
          useExisting: VpnRequestApiClient,
        },
        AssetOrAccessoryRequestApiClient,
        {
          provide: 'assetOrAccessoryRequestApiClient',
          useExisting: AssetOrAccessoryRequestApiClient,
        },
        TransferOrUnloadingRequestApiClient,
        {
          provide: 'transferOrUnloadingRequestApiClient',
          useExisting: TransferOrUnloadingRequestApiClient,
        },
        OutOfOrderAssetRequestApiClient,
        {
          provide: 'outOfOrderAssetRequestApiClient',
          useExisting: OutOfOrderAssetRequestApiClient,
        },
        MedicalGasRequestApiClient,
        {
          provide: 'medicalGasRequestApiClient',
          useExisting: MedicalGasRequestApiClient,
        },
        CorrectiveMaintenanceStatisticsApiClient,
        {
          provide: 'correctiveMaintenanceStatisticsApiClient',
          useExisting: CorrectiveMaintenanceStatisticsApiClient,
        },
        AssetStatusHistoryApiClient,
        {
          provide: 'assetStatusHistoryApiClient',
          useExisting: AssetStatusHistoryApiClient,
        },
        GoodToBeTransferredOrUnloadedApiClient,
        {
          provide: 'goodToBeTransferredOrUnloadedApiClient',
          useExisting: GoodToBeTransferredOrUnloadedApiClient,
        },
        AssetToBeTransferredOrUnloadedApiClient,
        {
          provide: 'assetToBeTransferredOrUnloadedApiClient',
          useExisting: AssetToBeTransferredOrUnloadedApiClient,
        },
      ],
    };
  }
}
